/**
 * /settings → "Connecter un autre appareil" — mints a pairing code
 * (`POST /api/pairing/create`) for signing in on a second device (typically
 * an iOS PWA install, which can't receive the magic-link email) via
 * /login/code there. Big, letter-spaced Gabarito display + a live
 * expiry countdown (memoneo's pairing codes are 5-minute, one-shot) + Copy.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy, Check } from "lucide-react";
import { Card, Button, MutedText, ErrorBanner } from "./ui";
import { pairingCreate } from "../lib/dataSource";
import { currentUserId } from "../lib/api";

function formatCountdown(msRemaining: number): string {
  const totalSeconds = Math.max(0, Math.ceil(msRemaining / 1000));
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function PairingPanel() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [copied, setCopied] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  async function requestCode() {
    setLoading(true);
    setError(false);
    setCopied(false);
    try {
      const res = await pairingCreate(currentUserId() ?? "");
      setCode(res.code);
      setExpiresAt(new Date(res.expires_at).getTime());
    } catch {
      setError(true);
      setCode(null);
      setExpiresAt(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!open) return;
    void requestCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || !expiresAt) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [open, expiresAt]);

  const remainingMs = expiresAt ? expiresAt - now : 0;
  const expired = expiresAt !== null && remainingMs <= 0;

  async function copyCode() {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable — the code is still visible to copy by hand */
    }
  }

  return (
    <section>
      <h2 className="font-heading font-bold text-lg mb-3">{t("settings.pairing.title")}</h2>
      {!open ? (
        <Button variant="secondary" onClick={() => setOpen(true)} className="w-full">
          {t("settings.pairing.launch")}
        </Button>
      ) : (
        <Card className="flex flex-col items-center gap-3 text-center">
          {error && <ErrorBanner>{t("settings.pairing.error")}</ErrorBanner>}
          {loading && !code && <MutedText>{t("common.loading")}</MutedText>}
          {code && (
            <>
              <p className="headline text-4xl font-heading tracking-[0.2em]" aria-live="polite">
                {code}
              </p>
              <MutedText>
                {expired
                  ? t("settings.pairing.expired")
                  : t("settings.pairing.expiresIn", { time: formatCountdown(remainingMs) })}
              </MutedText>
              <MutedText>{t("settings.pairing.hint")}</MutedText>
              <div className="flex gap-2 w-full">
                {expired ? (
                  <Button className="flex-1" onClick={() => void requestCode()} disabled={loading}>
                    {t("settings.pairing.regenerate")}
                  </Button>
                ) : (
                  <Button variant="secondary" className="flex-1" onClick={() => void copyCode()}>
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                    {copied ? t("settings.pairing.copied") : t("settings.pairing.copy")}
                  </Button>
                )}
              </div>
            </>
          )}
          <button
            type="button"
            className="text-xs text-violet-muted font-heading uppercase tracking-wide"
            onClick={() => setOpen(false)}
          >
            {t("common.close")}
          </button>
        </Card>
      )}
    </section>
  );
}
